import React, {useEffect} from "react";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button } from "../Button";
import { Input } from "../Input";
import {
  // GoogleIcon,
  // AppleIcon,
  // FacebookIcon,
  MessageIcon,
} from "../../assets/icons";
import { useDarkMode } from "../../context/DarkModeContext";
import DataUpLogo from "../../assets/images/DataUpLogoAuth.svg";
import { login, loginGoogle } from "../../features/store/authSlice";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import {
  LoginFormContainerProps,
  LoginFormProps,
  FooterLinksProps,
  FormData,
} from "./types";
import useClickOutside from "../../hooks/useClickOutside";
import { useSocialLogin } from "../../context/SocialLoginConnect";

export const LoginFormContainer: React.FC<LoginFormContainerProps> = ({
  onForgotPassword,
  onSignUp,
  onSubmit,
  error,
  user,
  closeLoginModal, // <-- Ensure this is included
}) => {
  const clickRef = useClickOutside(closeLoginModal);
 const { showGoogleConnectPrompt, setShowGoogleConnectPrompt } = useSocialLogin();
  const SocialLoginButtons = () => (
    <div className="flex gap-4 justify-center mb-4">
      {/* <SocialButton icon={<GoogleIcon />} /> */}
      <GoogleLogin
        onSuccess={handleGoogleLogin}
        onError={() => toast.error("Google login failed.")}
      />
    </div>
  );

  const dispatch = useAppDispatch();
 

  const handleGoogleLogin = async (credentialResponse: any) => {

    if (!credentialResponse?.credential) {
      toast.error("Google login failed.");
      return;
    }
    try {
      await dispatch(loginGoogle(credentialResponse.credential)).unwrap();
    if (showGoogleConnectPrompt) {
        toast.success("🎉 Google account linked successfully!");
        setShowGoogleConnectPrompt(false);
      } else {
        toast.success("🎉 Logged in successfully!");
      }
      closeLoginModal();
    } catch (error: any) {
      toast.error(error.message || "Google login failed.");
    }
  };

  return (
    <div ref={clickRef}>
      <Logo />
      <Title />
      <div className="space-y-4">
        <SocialLoginButtons />

        <OrDivider />
        <LoginForm
          onSubmit={onSubmit}
          onForgotPassword={onForgotPassword}
          closeLoginModal={closeLoginModal}
        />
        <StatusMessages error={error} user={user} />
        <FooterLinks onForgotPassword={onForgotPassword} onSignUp={onSignUp} />
      </div>
    </div>
  );
};

const Logo = () => (
  <div className="flex justify-center mb-4">
    <img
      src={DataUpLogo}
      alt="DatauplogoAuth"
      className="w-[135.55px] h-[32px]"
    />
  </div>
);

const Title = () => (
  <h2 className="text-2xl font-bold text-center mb-6 dark:text-white">
    Welcome back
  </h2>
);

// const SocialButton = ({ icon }: { icon: React.ReactNode }) => (
//   <button className="p-2 border rounded-md w-32 flex items-center justify-center">
//     {icon}
//   </button>
// );

const OrDivider = () => (
  <div className="flex items-center my-4">
    <div className="flex-1 border-t border-gray-300"></div>
    <div className="px-4 text-gray-500 text-sm dark:text-white">OR</div>
    <div className="flex-1 border-t border-gray-300"></div>
  </div>
);

const LoginForm = ({ onForgotPassword, closeLoginModal }: LoginFormProps) => {
  const schema = yup
    .object({
      identifier: yup.string().required().label("Email"),
      password: yup.string().required().label("Password"),
    })
    .required();

  const { register, handleSubmit, reset } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const dispatch = useAppDispatch();
  const { darkMode } = useDarkMode();
  const { error } = useAppSelector((state) => state.auth);

  const onFormSubmit = async (data: FormData) => {
    await dispatch(login(data))
      .unwrap()
      .then(() => {
        toast.success("🎉 Logged in successfully!");
        reset();
        closeLoginModal(); // navigate("/dashboard");
      })
      .catch(() => {
        toast.error(error);
      });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <Input
        type="text"
        placeholder="Email address/Username"
        icon={<MessageIcon theme={darkMode ? "dark" : "light"} />}
        register={register}
        name="identifier"
        required
      />

      <div className="space-y-2">
        <Input
          type="password"
          placeholder="Password"
          isPassword
          register={register}
          name="password"
          required
        />
        <div className="text-right">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-xs font-bold dark:text-white hover:underline"
          >
            Forgot Password?
          </button>
        </div>
      </div>

      <Button
        type="submit"
        bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
        textColor="text-white"
        borderRadius="rounded-lg"
        className="w-full h-10"
      >
        Sign In
      </Button>
    </form>
  );
};

const StatusMessages = ({
  error,
  user,
}: {
  error?: string | null;
  user?: any;
}) => (
  <>
    {error && <p className="text-red-500">{error}</p>}
    {user && <p className="text-green-500">Logged In!</p>}
  </>
);

const FooterLinks = ({ onSignUp }: FooterLinksProps) => (
  <>
    <p className="text-sm md:text-base dark:text-white text-center">
      Don't have an account?{" "}
      <span className="text-[#039EF2] cursor-pointer" onClick={onSignUp}>
        Sign Up
      </span>
    </p>
  </>
);
export default LoginFormContainer;
